import client from './client';

/**
 * WCDP IAS export (TAS-682) — the board's replacement for the V2 Batch Export and Paycor V2
 * pages. Every call goes to /admin/wcdp/iasExport (API src/routes/admin/wcdpIasExport.ts):
 * 404 when the tenant is not WCDP, 403 without ExportPayroll, and every error body carries
 * `errorMessage`.
 *
 * - POST /export          one supervisor's crew → Solomon TimeEntry_App (+ per-employee PRDA)
 * - GET  /status          who is recorded as exported this pay period (board chips)
 * - GET  /batchReport     the legacy Regular / Direct Deposit batch report (xlsx)
 * - GET  /paycorPreview   the counts for the "Generate Paycor File" confirm
 * - POST /paycorFile      the Paycor zip for IAS-exported, still-approved employees
 */

const BASE = '/admin/wcdp/iasExport';

/* ───────────────────────────────────────────────────────────────── types */

export interface WcdpExportResult {
    exportID: string;
    batchID: string;
    batchID_DD: string;
    employeeCount: number;
    rowCount: number;
    regularRowCount: number;
    ddRowCount: number;
    noRowsCount: number;
    forcedEmployeeCount: number;
    prdaEmployeeCount: number;
    prdaRecordCount: number;
    message: string;
    /** Set on a forced export: the rows are now in IAS twice unless the earlier batch is deleted. */
    warning?: string;
}

/** 409 — selected employees already exported this pay period; resend with force. */
export interface WcdpAlreadyExported {
    employeeID: number;
    altEmployeeID: string;
    name: string;
    exportedAt: string;
    batchID: string;
    /** false = the earlier export's IAS commit was never confirmed (it MAY be in Solomon). */
    confirmed: boolean;
}

export type WcdpRejectionReason =
    | 'notFound'
    | 'notPayrollApproved'
    | 'notInCrew'
    | 'missingAltEmployeeID'
    | 'timecardFailed'
    | 'earningsCodeTooLong'
    | 'prdaAdjustmentExists'
    | 'timesheetChanged';

/** 422 — the whole export was refused (nothing written); these employees are why. */
export interface WcdpRejection {
    employeeID: number;
    altEmployeeID: string;
    name: string;
    reason: WcdpRejectionReason | string;
    message: string;
}

/** An employee's LATEST IAS export this pay period. Only employees with any export are listed. */
export interface WcdpExportedInfo {
    employeeID: number;
    /** ISO timestamp. */
    exportedAt: string;
    batchID: string;
    batchID_DD: string;
    supervisorID: number;
    /** The export carried no IAS rows for this employee (recorded, nothing sent). */
    noRows: boolean;
    exportCount: number;
    /** false = IAS's commit outcome was unknown; the rows MAY be in Solomon. */
    confirmed: boolean;
    stillApproved: boolean;
    /** Exported TimesheetID ≠ the current Payroll Approved TimesheetID (or no longer approved). */
    changedSinceExport: boolean;
}

export interface WcdpExportStatus {
    payPeriodID: number;
    employees: WcdpExportedInfo[];
}

export interface WcdpPaycorPreview {
    includedCount: number;
    approvedNotExportedCount: number;
    exportedNotApproved: { employeeID: number; altEmployeeID: string; name: string; exportedAt: string }[];
}

/**
 * Every failure of the calls above. `status` is the HTTP status when the failure came off the
 * wire. The extra fields mirror the API's error bodies:
 * - 409 `alreadyExported`
 * - 422 `rejections` (export) / `failures` (Paycor)
 * - 502 `unconfirmed` + `exportID` + batch IDs — IAS may have committed; recorded as unconfirmed
 * - 500 `iasCommitted` + batch IDs — IAS committed but nothing could be recorded: do NOT re-export
 */
export class WcdpExportError extends Error {
    status?: number;
    alreadyExported?: WcdpAlreadyExported[];
    rejections?: WcdpRejection[];
    failures?: unknown[];
    unconfirmed?: boolean;
    iasCommitted?: boolean;
    exportID?: string;
    batchID?: string;
    batchID_DD?: string;
    constructor(
        message: string,
        extra?: {
            status?: number;
            alreadyExported?: WcdpAlreadyExported[];
            rejections?: WcdpRejection[];
            failures?: unknown[];
            unconfirmed?: boolean;
            iasCommitted?: boolean;
            exportID?: string;
            batchID?: string;
            batchID_DD?: string;
        }
    ) {
        super(message);
        this.name = 'WcdpExportError';
        this.status = extra?.status;
        this.alreadyExported = extra?.alreadyExported;
        this.rejections = extra?.rejections;
        this.failures = extra?.failures;
        this.unconfirmed = extra?.unconfirmed;
        this.iasCommitted = extra?.iasCommitted;
        this.exportID = extra?.exportID;
        this.batchID = extra?.batchID;
        this.batchID_DD = extra?.batchID_DD;
    }
}

/* ─────────────────────────────────────────────────────────────── helpers */

/**
 * Turns an axios failure into a WcdpExportError. The blob downloads (batch report, Paycor zip)
 * receive their JSON error body as a Blob, so it is read back off the wire first — the same
 * treatment decodeSageError gives the 2020PH Sage file (api/2020ph.ts).
 */
export async function decodeWcdpError(error: unknown, fallback: string): Promise<WcdpExportError> {
    if (error instanceof WcdpExportError) return error;
    const response = (error as { response?: { data?: unknown; status?: number } })?.response;
    const status = typeof response?.status === 'number' ? response.status : undefined;
    let body: unknown = response?.data;
    if (typeof Blob !== 'undefined' && body instanceof Blob) {
        const text = await body.text().catch(() => '');
        try {
            body = JSON.parse(text);
        } catch {
            body = text;
        }
    }
    if (typeof body === 'string' && body.trim() && !body.trim().startsWith('<')) {
        return new WcdpExportError(body.trim(), { status });
    }
    const obj = (body && typeof body === 'object' ? body : null) as {
        errorMessage?: string;
        message?: string;
        alreadyExported?: WcdpAlreadyExported[];
        rejections?: WcdpRejection[];
        failures?: unknown[];
        unconfirmed?: boolean;
        iasCommitted?: boolean;
        exportID?: string;
        batchID?: string;
        batchID_DD?: string;
    } | null;
    const message =
        obj?.errorMessage
        || obj?.message
        || (status === 404 ? 'Not found.' : '')
        || (error instanceof Error && error.message ? error.message : fallback);
    return new WcdpExportError(message, {
        status,
        alreadyExported: Array.isArray(obj?.alreadyExported) ? obj?.alreadyExported : undefined,
        rejections: Array.isArray(obj?.rejections) ? obj?.rejections : undefined,
        failures: Array.isArray(obj?.failures) ? obj?.failures : undefined,
        unconfirmed: obj?.unconfirmed === true ? true : undefined,
        iasCommitted: obj?.iasCommitted === true ? true : undefined,
        exportID: typeof obj?.exportID === 'string' ? obj.exportID : undefined,
        batchID: typeof obj?.batchID === 'string' ? obj.batchID : undefined,
        batchID_DD: typeof obj?.batchID_DD === 'string' ? obj.batchID_DD : undefined,
    });
}

/** Saves a blob response; the server's Content-Disposition name wins over the fallback. */
function saveBlobResponse(response: { data: unknown; headers: unknown }, fallbackFileName: string): void {
    const disposition = (response.headers as Record<string, string | undefined> | undefined)?.['content-disposition'];
    const match = disposition?.match(/filename[^;=\n]*=(['"]?)([^'";\n]+)\1/);
    const fileName = match ? match[2].trim() : fallbackFileName;

    const url = URL.createObjectURL(response.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, 100);
}

/* ───────────────────────────────────────────────────────────────── calls */

/**
 * Export one crew's selected employees (internal EmployeeIDs) to IAS. `force` goes on the
 * wire only when true — it re-exports employees already recorded this pay period.
 */
export async function exportWcdpIas(
    supervisorID: number,
    payPeriodID: number,
    employeeIDs: number[],
    force?: boolean
): Promise<WcdpExportResult> {
    const body: { supervisorID: number; payPeriodID: number; employeeIDs: number[]; force?: true } = {
        supervisorID,
        payPeriodID,
        employeeIDs,
    };
    if (force === true) body.force = true;
    try {
        const res = await client.post(`${BASE}/export`, body);
        return res.data as WcdpExportResult;
    } catch (error) {
        throw await decodeWcdpError(error, 'The IAS export failed.');
    }
}

export async function getWcdpIasExportStatus(payPeriodID: number): Promise<WcdpExportStatus> {
    try {
        const res = await client.get(`${BASE}/status`, { params: { payPeriodID } });
        return res.data as WcdpExportStatus;
    } catch (error) {
        throw await decodeWcdpError(error, 'Could not load the IAS export status.');
    }
}

/** The legacy batch report for one batch ID (regular `batchID` or direct-deposit `batchID_DD`). */
export async function downloadWcdpBatchReport(batchID: string): Promise<void> {
    let response;
    try {
        response = await client.get(`${BASE}/batchReport`, {
            params: { batchID },
            responseType: 'blob',
        });
    } catch (error) {
        throw await decodeWcdpError(error, 'Could not generate the batch report.');
    }
    saveBlobResponse(response, `${batchID}.xlsx`);
}

export async function getWcdpPaycorPreview(payPeriodID: number): Promise<WcdpPaycorPreview> {
    try {
        const res = await client.get(`${BASE}/paycorPreview`, { params: { payPeriodID } });
        return res.data as WcdpPaycorPreview;
    } catch (error) {
        throw await decodeWcdpError(error, 'Could not load the Paycor preview.');
    }
}

/** Generates (and records) the Paycor zip for the whole pay period, then saves it. */
export async function downloadWcdpPaycorFile(payPeriodID: number): Promise<void> {
    let response;
    try {
        response = await client.post(`${BASE}/paycorFile`, null, {
            params: { payPeriodID },
            responseType: 'blob',
        });
    } catch (error) {
        throw await decodeWcdpError(error, 'Could not generate the Paycor file.');
    }
    saveBlobResponse(response, `paycor-file-${payPeriodID}.zip`);
}
